import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import {
  useGetEnvironmentsQuery,
  useGetEnvironmentVariablesQuery,
  useGetGlobalVariables,
} from "../../useQuery";
import { createScopeResolver, asMap } from "../engine/resolver";
import { tokenizeText, interpolateText } from "../engine/interpolate";
import { BUILTIN_PROVIDER_METADATA } from "../providers/providerRegistry";

export const GLOBAL_OPTION = "__GLOBAL__";
export const NONE_OPTION = "__NONE__";

const VariableStoreContext = createContext(null);

export const VariableProvider = ({ children }) => {
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState(null);

  const { data: environmentsData } = useGetEnvironmentsQuery({ enabled: true });
  const { data: globalVariablesData, refetch: refetchGlobalVariables } = useGetGlobalVariables({
    enabled: true,
    refetchOnMount: true,
  });
  const { data: environmentVariablesData } = useGetEnvironmentVariablesQuery(
    selectedEnvironmentId,
    {
      enabled:
        !!selectedEnvironmentId &&
        selectedEnvironmentId !== GLOBAL_OPTION &&
        selectedEnvironmentId !== NONE_OPTION,
      keepPreviousData: true,
    },
  );

  useEffect(() => {
    if (!selectedEnvironmentId && Array.isArray(environmentsData) && environmentsData.length > 0) {
      setSelectedEnvironmentId(environmentsData[0].id);
    }
  }, [environmentsData, selectedEnvironmentId]);

  useEffect(() => {
    if (selectedEnvironmentId === GLOBAL_OPTION) {
      refetchGlobalVariables();
    }
  }, [selectedEnvironmentId, refetchGlobalVariables]);

  const globalScope = useMemo(() => asMap(globalVariablesData || []), [globalVariablesData]);

  const environmentScope = useMemo(
    () => asMap(environmentVariablesData || []),
    [environmentVariablesData],
  );

  const resolver = useMemo(() => {
    if (selectedEnvironmentId === GLOBAL_OPTION) {
      return createScopeResolver(globalScope, {});
    }
    if (selectedEnvironmentId === NONE_OPTION || selectedEnvironmentId === null) {
      return createScopeResolver({}, {});
    }
    return createScopeResolver(globalScope, environmentScope);
  }, [globalScope, environmentScope, selectedEnvironmentId]);

  const getAllVariables = useMemo(() => {
    const globalList = (globalVariablesData || []).map((v) => ({
      name: v.variable,
      value: v.value,
      scope: "global",
    }));
    const envList = (environmentVariablesData || []).map((v) => ({
      name: v.variable,
      value: v.value,
      scope: "environment",
    }));
    return [...envList, ...globalList];
  }, [globalVariablesData, environmentVariablesData]);

  const getVariableByName = (name) => {
    const result = resolver.resolveVariable(name);
    return result.source !== "unknown" ? result : undefined;
  };

  const getEnvironmentVariables = () => {
    if (
      selectedEnvironmentId &&
      selectedEnvironmentId !== GLOBAL_OPTION &&
      selectedEnvironmentId !== NONE_OPTION
    ) {
      return environmentScope;
    }
    return {};
  };

  const getGlobalVariables = () => globalScope;

  const getGlobalVariable = (name) => globalScope[name];

  const getEnvironmentVariable = (name) => environmentScope[name];

  const contextValue = {
    environments: environmentsData || [],
    selectedEnvironmentId,
    setSelectedEnvironmentId,
    environmentVariables: environmentVariablesData || [],
    globalVariables: globalVariablesData || [],

    tokenize: (text) => tokenizeText(text, resolver),
    interpolate: (text) => interpolateText(text, resolver),

    GLOBAL_OPTION,
    NONE_OPTION,

    getAllVariables,
    getVariableByName,
    getEnvironmentVariables,
    getGlobalVariables,
    getGlobalVariable,
    getEnvironmentVariable,

    dynamicProviders: BUILTIN_PROVIDER_METADATA,

    _resolver: resolver,
  };

  return (
    <VariableStoreContext.Provider value={contextValue}>{children}</VariableStoreContext.Provider>
  );
};

export const useVariables = () => {
  const context = useContext(VariableStoreContext);
  if (!context) {
    throw new Error("useVariables must be used within a VariableProvider");
  }
  return context;
};
